import { Key, useCallback, useMemo } from 'react';

import { format } from 'date-fns';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { OrganizationMember, OrganizationRole } from 'src/shared/types/user';

import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

import { membersListColumns } from '../constants/membersListColumns';
import MemberNameCell from './MemberNameCell';

const MembersList = () => {
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  const total = useMemo(() => {
    return selectedOrganization?.members?.length;
  }, [selectedOrganization]);

  const data = useMemo(() => {
    return selectedOrganization?.members;
  }, [selectedOrganization]);

  const renderCell = useCallback((item: OrganizationMember, columnKey: Key) => {
    switch (columnKey) {
      case 'name':
        return <MemberNameCell name={item?.email} image={item.avatar_url} />;

      case 'role':
        return (
          <Typography variant="body-1" weight="medium">
            {item.role === OrganizationRole.ADMIN ? 'Admin' : 'Member'}
          </Typography>
        );

      case 'joined_on':
        return (
          <Typography variant="body-1" weight="medium">
            {format(new Date(item.date_joined), 'MMM dd, yyyy')}
          </Typography>
        );

      case 'last_login':
        return (
          <Typography variant="body-1" weight="medium">
            {item.last_login
              ? format(new Date(item.last_login), 'MMM dd, yyyy')
              : 'Never'}
          </Typography>
        );

      default:
        return '';
    }
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center">
        <Typography variant="h4" weight="bold">
          {total} Members
        </Typography>
        {/* <ButtonV2 color="dark">Invite new member</ButtonV2> */}
      </div>

      <Table
        aria-label="Products table"
        className="table-auto mt-2"
        removeWrapper
      >
        <TableHeader columns={membersListColumns}>
          {(column) => (
            <TableColumn
              className="bg-white border-b border-gray-4"
              key={column.key}
            >
              <Typography
                variant="body-2"
                weight="medium"
                className="text-secondary-text"
              >
                {column.label}
              </Typography>
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={data ?? []}>
          {(item) => (
            <TableRow
              className="h-16 transition-background border-b border-gray-4"
              key={item.id}
            >
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default MembersList;
